import * as React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { Grid, makeStyles } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { CollectionList } from './CollectionList';
import CustomPopupText from '../../shared/CustomPopupText';
import CustomButton from '../../shared/CustomButton';
import service from '../../../apis/service';
import { apiEndpoints } from '../../../constants/apiEndpoints';
import { ErrorToast, notifySuccess } from '../../utils/Utils';
import { showProgress } from '../../../redux/actions/DealershipActions';
import { findCategoryById } from '../utils';
import useCategory from '../hooks/useCategory';

const style = {
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '527px',
  backgroundColor: '#F2F4F7',
};

const useStyle = makeStyles({
  headerConatiner: {
    width: '527px',
    height: '72px',
    padding: '24px 16px 24px 16px',
    background: 'white',
  },
  contentContainer: {
    width: '527px',
    height: '579px',
  },

  buttonContainer: {
    marginTop: 'auto',
    background: '#FFFFFF',
    width: '527px',
    height: '64px',
    border: '1px',
  },
});

export default function AddCollectionsPopup({ isOpen, handleClose, categoryId, deptName, deptId }) {
  const classes = useStyle();
  const dispatch = useDispatch();
  const { fetchPartnerTypes } = useCategory();
  const content = useSelector((state) => state.categoryData.selectedCategoryDeptCollections);
  const selectedId = useSelector((state) => state.categoryData.selectedId);
  const catList = useSelector((state) => state.categoryData.categoryList);
  const [data, setData] = React.useState({ exploreCategoryId: categoryId, collectionIds: [] });
  const [collections, setCollections] = React.useState([]);
  React.useEffect(() => {
    resetData();
  }, [content]);
  React.useEffect(() => {
    getCollections();
  }, []);
  const resetData = React.useCallback(() => {
    const updatedCollections = findCategoryById(content, categoryId)?.collections?.flatMap((col) => col.id);
    setData((prev) => ({ ...prev, collectionIds: updatedCollections }));
  }, [categoryId, content]);
  const handleData = ({ target: { checked, value } }) => {
    setData((prev) => {
      const updatedCollections = checked
        ? [...new Set([...prev.collectionIds, parseInt(value, 10)])]
        : prev.collectionIds.filter((item) => item !== parseInt(value, 10));
      return { ...prev, collectionIds: updatedCollections };
    });
  };
  const addCollections = async (payload) => {
    dispatch(showProgress(true));
    try {
      await service.post(apiEndpoints.CAT_COLLECTION_UPDATE, payload);
      fetchPartnerTypes(selectedId);
      notifySuccess('Collection added successfully');
    } catch (error) {
      ErrorToast(error);
    }
    resetData();
    handleClose();
    dispatch(showProgress(false));
  };

  const getCollections = async () => {
    const payload = {
      rockedNetworkPartnerId: content?.rockedNetworkPartnerId,
      departmentId: deptId,
      exploreCategoryId: categoryId,
    };
    dispatch(showProgress(true));
    try {
      await service
        .post(apiEndpoints.CAT_CATEGORY_COLLECTIONS, payload)
        .then((response) => {
          setCollections(response);
        })
        .catch((err) => ErrorToast(err));
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const getType = () => catList?.filter((item) => item?.id === content?.rockedNetworkPartnerId)[0]?.type;

  const handleSave = () => {
    addCollections(data);
  };

  return (
    <div>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            className={classes.headerConatiner}
          >
            <Grid item>
              <CustomPopupText
                text={`Add Collections to ${findCategoryById(content, categoryId)?.name}`}
                fontSize="16px"
                fontWeight="600"
                color="#000"
              />
            </Grid>
            <Grid item>
              <Close
                onClick={() => {
                  resetData();
                  handleClose();
                }}
                style={{ cursor: 'pointer' }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            direction="column"
            style={{
              borderRadius: '8px',
              background: 'white',
              margin: '10px 10px 0px 10px',
              width: '495px',
            }}
          >
            <Grid item>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{ padding: '8px 16px 8px 16px' }}
              >
                <Grid item>
                  <CustomPopupText text="Content Type" color="#667085" />
                </Grid>
                <Grid item>
                  <CustomPopupText text={getType()} color="#10182B" />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{ padding: '8px 16px 8px 16px' }}
              >
                <Grid item>
                  <CustomPopupText text="Department" color="#667085" />
                </Grid>
                <Grid item className={classes.contentTypeText}>
                  <CustomPopupText text={deptName} color="#10182B" />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <CollectionList
            selected={data?.collectionIds}
            collections={collections}
            className={classes.contentContainer}
            handleData={handleData}
          />

          <Grid container justifyContent="flex-end" style={{ height: '100%' }}>
            <Grid item className={classes.buttonContainer}>
              <Grid container direction="row" alignItems="center" justifyContent="flex-end" style={{ height: '100%' }}>
                <Grid item style={{ marginRight: '6px' }}>
                  <CustomButton
                    label="Cancel"
                    type="secondary"
                    onClick={() => {
                      resetData();
                      handleClose();
                    }}
                  />
                </Grid>
                <Grid item style={{ marginRight: '6px' }}>
                  <CustomButton label="Save" type="primary" onClick={handleSave} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
}
